/**
 * WHOSE BOOK AM I LOOKING AT — the advisor pivot.
 *
 * Taylor, 2026-08-10: "I should as an admin be able to choose a customer, or pivot
 * to see any AE or csm and pivot to see their customers and where everything is at.
 * And I really expect all internal users to see all customers. Only admins or
 * manager level should be able to pivot to just see csms and aes."
 *
 * So there are two separate things, and conflating them is the mistake to avoid:
 *
 *   VISIBILITY (unchanged) — every internal user already sees every customer. The
 *   one exception is crm:btp_only, scoped server-side to BTP-enrolled rows. This
 *   module does not touch visibility.
 *
 *   PIVOT (new) — which advisor's book the "mine" sections of a page describe.
 *   Confirmed by Taylor as a VIEW FILTER, not a data scope: the rows are already
 *   loaded and already permitted, this only changes which subset is highlighted.
 *   That is why it lives in the client and needs no API change.
 *
 * BECAUSE IT IS NOT A SECURITY BOUNDARY, the gate here is an affordance: it decides
 * who is OFFERED the pivot control, not who is allowed to see data. Nothing is
 * hidden from a CSM that they could not already reach by other means — the customers
 * list has always shown every AE's rows. If the pivot ever starts fetching data a
 * role cannot otherwise get, it stops being a view filter and needs a server gate.
 *
 * THE GATE IS `manage:ae_routing`, held by exactly `admin` and `crm:manager` (see
 * features/crm/auth/roles.ts ROLE_CAPS — csm, btp_only and viewer do not hold it).
 * That is "manager level" as described, expressed with a capability cm-api already
 * enforces, rather than inventing a new one the backend would not recognise. It is
 * also the closest in meaning: the capability about who owns which account.
 */
import type { Capability } from '../auth/roles';

/** The capability that marks "manager level" for pivot purposes. */
export const PIVOT_CAPABILITY: Capability = 'manage:ae_routing';

/**
 * May this user pivot to somebody ELSE's book?
 *
 * Everyone can always see their own — that is the default view and needs no
 * permission. This is strictly about viewing another advisor's book.
 */
export function canPivotToOthers(
  can: ((c: Capability) => boolean) | undefined,
): boolean {
  // FAIL CLOSED when the capability resolver isn't available yet (the /me context
  // still loading, or a caller that didn't provide it). A gate that throws takes
  // the whole page down; a gate that defaults to "allowed" hands out the pivot.
  // Neither is acceptable, so: no resolver ⇒ no pivot, and the control simply
  // isn't offered until we know.
  if (typeof can !== 'function') return false;
  return can(PIVOT_CAPABILITY);
}

/**
 * May this user ADD other advisors alongside their own book?
 *
 * Distinct from canPivotToOthers, and the distinction matters. Taylor's rule was
 * "only admins or manager level should be able to pivot to just see csms and aes",
 * which I first implemented by removing the AE/CSM multi-selects from non-managers
 * entirely. That also removed a deliberate existing behaviour: the selects are
 * multi-select so a CSM could tick themselves PLUS a colleague and see co-covered
 * accounts (the comment on that filter row says exactly this).
 *
 * So the two are now separated:
 *   - ADD (everyone): widen your own view to include a partner. Your book is still
 *     in it, so you are not "seeing someone else's book instead of yours".
 *   - REPLACE (manager level, canPivotToOthers): view another advisor's book on its
 *     own, which is the thing the rule was about.
 *
 * Nothing here is a data boundary either way — every internal user can already see
 * every customer, and the list has always shown every AE's rows.
 */
export function canAddOtherAdvisors(): boolean {
  return true;
}

/** An advisor who owns at least one customer, as the pivot list offers them. */
export type Advisor = {
  /** Display name exactly as stored on the customer row. */
  name: string;
  /** How this person relates to the customers counted below. */
  roles: ('AE' | 'CSM')[];
  customerCount: number;
};

type BookRow = {
  cust_ae?: string | null;
  cust_csm?: string | null;
  /**
   * STABLE ADVISOR IDENTITY, preferred over the names above.
   *
   * Domo's Customer Master has carried an email and an id per advisor role all
   * along; the CRM just never selected them, so "whose book is this" was a
   * display-name string compare — which emptied a book on a middle initial and
   * broke permanently on a name change. These are now returned by
   * /api/customers and are the primary match key.
   */
  cust_ae_email?: string | null;
  cust_ae_id?: string | null;
  cust_csm_email?: string | null;
  cust_csm_id?: string | null;
};

/** Who we're matching a book against, most-stable key first. */
export type AdvisorIdentity = {
  /** Domo advisor id — survives renames. */
  id?: string | null;
  email?: string | null;
  name?: string | null;
};

function eq(a: string | null | undefined, b: string | null | undefined) {
  if (!a || !b) return false;
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}

/**
 * Does this row belong to this advisor, as AE or CSM?
 *
 * Tried in order of durability — id, then email, then display name. The name
 * remains a fallback rather than being dropped, because a row whose advisor id is
 * null (older data, or a customer seeded before the columns existed) would
 * otherwise fall out of a book that legitimately contains it.
 */
export function rowBelongsTo(
  row: BookRow,
  who: AdvisorIdentity,
): 'AE' | 'CSM' | null {
  if (who.id) {
    if (eq(row.cust_ae_id, who.id)) return 'AE';
    if (eq(row.cust_csm_id, who.id)) return 'CSM';
  }
  if (who.email) {
    if (eq(row.cust_ae_email, who.email)) return 'AE';
    if (eq(row.cust_csm_email, who.email)) return 'CSM';
  }
  if (who.name) {
    if (eq(row.cust_ae, who.name)) return 'AE';
    if (eq(row.cust_csm, who.name)) return 'CSM';
  }
  return null;
}

/**
 * Every advisor present in the loaded customers, with how many they own.
 *
 * Derived from the ROWS rather than a user directory on purpose: the pivot exists to
 * answer "whose customers are these", and the only names that can possibly match
 * `cust_ae` / `cust_csm` are the ones already written there. Offering a name from
 * the directory that does not appear on any row would produce a confidently empty
 * book — the same class of lie as the old 'Dev User' filter.
 */
export function advisorsFrom(rows: readonly BookRow[]): Advisor[] {
  const byName = new Map<string, { ae: number; csm: number }>();
  for (const row of rows) {
    const ae = row.cust_ae?.trim();
    const csm = row.cust_csm?.trim();
    if (ae) {
      const e = byName.get(ae) ?? { ae: 0, csm: 0 };
      e.ae += 1;
      byName.set(ae, e);
    }
    // A person can be the AE on some accounts and the CSM on others; count both
    // but never double-count the SAME row against one name.
    if (csm && csm !== ae) {
      const e = byName.get(csm) ?? { ae: 0, csm: 0 };
      e.csm += 1;
      byName.set(csm, e);
    }
  }

  return [...byName.entries()]
    .map(([name, { ae, csm }]) => {
      const roles: ('AE' | 'CSM')[] = [];
      if (ae > 0) roles.push('AE');
      if (csm > 0) roles.push('CSM');
      return { name, roles, customerCount: ae + csm };
    })
    .sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * The rows belonging to one advisor, as either AE or CSM.
 *
 * Exact, case-insensitive name comparison — the same rule the rest of the CRM uses,
 * kept identical on purpose so the pivot and the self-view can never disagree about
 * what "someone's book" means. (That rule is fragile: cust_ae holds a display name,
 * so a middle initial or a name change empties a book. Fixing it needs an advisor
 * identifier in the CRM data, not a cleverer comparison here.)
 */
export function bookFor<T extends BookRow>(
  rows: readonly T[],
  advisor: string | AdvisorIdentity | null,
): T[] {
  const who = normalizeAdvisor(advisor);
  if (!who) return [];
  return rows.filter((r) => rowBelongsTo(r, who) !== null);
}

/**
 * Accept either a bare display name (the pivot's selection, which comes from the
 * rows) or a full identity (the signed-in user, who has an email). Keeps every
 * caller on one matching rule instead of each re-deriving it.
 */
function normalizeAdvisor(
  advisor: string | AdvisorIdentity | null,
): AdvisorIdentity | null {
  if (advisor === null) return null;
  if (typeof advisor === 'string') {
    const name = advisor.trim();
    return name ? { name } : null;
  }
  const hasAny = !!(advisor.id || advisor.email || advisor.name);
  return hasAny ? advisor : null;
}

/** How the viewed advisor relates to one row — for the "AE / CSM" column. */
export function relationTo(
  row: BookRow,
  advisor: string | AdvisorIdentity | null,
): 'AE' | 'CSM' | null {
  const who = normalizeAdvisor(advisor);
  if (!who) return null;
  return rowBelongsTo(row, who);
}

/**
 * ─────────────────────────────────────────────────────────────────────────────
 * MULTI-ADVISOR SELECTION (2026-09-08)
 *
 * Taylor: "ability to choose multiple csms or aes at the same time".
 *
 * The single-advisor helpers above stay — the Customers page and the customer
 * profile still ask "is this row Dana's?" one advisor at a time. These add the
 * set case, which Home needs: a manager comparing three CSMs, or a CSM looking
 * at their own book plus the colleague they cover for.
 *
 * THE EMPTY SELECTION MEANS EVERYONE, and that is a deliberate, load-bearing
 * choice rather than a convenience. The single-advisor API used `null` for
 * "everyone" and a name for "one person", which left no way to say "nobody" —
 * fine, because a book view always has a subject. Keeping the same convention
 * here (`[]` ⇒ every row) means clearing the control lands on the widest view
 * instead of a blank screen, which is what a filter control should do.
 *
 * Union, not intersection. Selecting Dana and Priya shows accounts EITHER of
 * them owns. Intersection would answer "which accounts do they co-cover", which
 * is a different and much rarer question, and getting it silently would look
 * like the filter was broken.
 */
export type AdvisorSelection = ReadonlyArray<string | AdvisorIdentity>;

/**
 * The union of several advisors' books.
 *
 * Deduplicated by identity of the row object, so an account where Dana is AE
 * and Priya is CSM appears ONCE when both are selected. The single-advisor
 * `bookFor` could not produce a duplicate; concatenating its results can, and a
 * doubled row silently doubles every KPI computed off the list.
 */
export function bookForMany<T extends BookRow>(
  rows: readonly T[],
  advisors: AdvisorSelection,
): T[] {
  if (advisors.length === 0) return [...rows];
  const who = advisors
    .map(normalizeAdvisor)
    .filter((a): a is AdvisorIdentity => a !== null);
  if (who.length === 0) return [];
  return rows.filter((r) => who.some((w) => rowBelongsTo(r, w) !== null));
}

/**
 * How the row relates to the SELECTION, for the "Role" column.
 *
 * Returns the first match in selection order, which is why Home puts the
 * signed-in user first: on an account you are AE of and a colleague is CSM of,
 * "AE" is the answer that matters to you.
 *
 * Null for the empty selection, because "AE or CSM?" has no answer across
 * everyone — the old single-advisor code assumed CSM whenever the AE did not
 * match, which mislabelled every row it did not own.
 */
export function relationToMany(
  row: BookRow,
  advisors: AdvisorSelection,
): 'AE' | 'CSM' | null {
  for (const a of advisors) {
    const who = normalizeAdvisor(a);
    if (!who) continue;
    const rel = rowBelongsTo(row, who);
    if (rel) return rel;
  }
  return null;
}

/**
 * A short label for whose book is on screen.
 *
 * Centralized because three surfaces render it and they must agree — a heading
 * that says "My book" over somebody else's numbers is the one genuine risk of a
 * pivot control, and it is a UI problem rather than a security one.
 */
export function describeSelection(
  advisors: AdvisorSelection,
  selfName: string,
): string {
  if (advisors.length === 0) return 'All customers';
  const names = advisors.map((a) =>
    typeof a === 'string' ? a : (a.name ?? a.email ?? ''),
  );
  const isSelf = (n: string) =>
    !!selfName && n.trim().toLowerCase() === selfName.trim().toLowerCase();

  if (names.length === 1)
    return isSelf(names[0]) ? 'My book' : `${names[0]}’s book`;
  if (names.length === 2)
    return names.map((n) => (isSelf(n) ? 'me' : n)).join(' + ');
  // Past two, naming them all makes an unreadable heading; the control below it
  // already shows exactly who is selected.
  return `${names.length} advisors`;
}
